import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

const REFERRAL_CODE_KEY = "giftmap_referral_code";

/**
 * useAutoCreateUser
 * - When user first authenticates via magic link, ensure a row exists in the `users` table
 * - Triggered after useAuth detects session is active
 * - Only inserts if no row exists yet (idempotent)
 * - Records referral if a referral code was used during signup
 */
export default function useAutoCreateUser(userId: string | null | undefined) {
  const hasRecordedReferral = useRef(false);

  useEffect(() => {
    if (!userId) return;

    const createUserRow = async () => {
      try {
        // Check if user already has a row
        const { data, error: fetchError } = await supabase
          .from("users")
          .select("id")
          .eq("id", userId)
          .maybeSingle();

        if (fetchError && fetchError.code !== "PGRST116") {
          // PGRST116 = no rows found, which is fine
          console.warn("useAutoCreateUser: fetch error", fetchError);
          return;
        }

        const isNewUser = !data;

        if (isNewUser) {
          // Get user email from auth
          const { data: { user } } = await supabase.auth.getUser();
          const userEmail = user?.email || null;

          // Insert new user row with defaults
          const { error: insertError } = await supabase.from("users").insert({
            id: userId,
            name: null,
            avatar_url: null,
            is_active: false,
            privacy_level: "precise",
            email: userEmail,
            has_password: false,
          });

          if (insertError) {
            console.warn("useAutoCreateUser: insert error", insertError);
          } else {
            console.log("useAutoCreateUser: created user row for", userId);

            // Save referral code to user record if present
            const simpleReferral = localStorage.getItem("giftmap_simple_referral");
            if (simpleReferral) {
              const { error: refUpdateError } = await supabase
                .from("users")
                .update({ referral_code: simpleReferral } as any)
                .eq("id", userId);

              if (!refUpdateError) {
                console.log("useAutoCreateUser: saved referral code", simpleReferral);
                localStorage.removeItem("giftmap_simple_referral");
              } else {
                console.warn("useAutoCreateUser: referral code save error", refUpdateError);
              }
            }
          }
        }

        // Record referral if code exists and hasn't been recorded yet
        if (!hasRecordedReferral.current) {
          const storedCode = localStorage.getItem(REFERRAL_CODE_KEY);
          if (storedCode) {
            hasRecordedReferral.current = true;
            try {
              const response = await supabase.functions.invoke("record-referral", {
                body: {
                  referralCode: storedCode,
                },
              });

              if (response.error) {
                console.warn("useAutoCreateUser: referral recording error", response.error);
              } else {
                console.log("useAutoCreateUser: referral recorded successfully");
                // Clear the referral code after successful recording
                localStorage.removeItem(REFERRAL_CODE_KEY);
              }
            } catch (e) {
              console.warn("useAutoCreateUser: referral recording exception", e);
            }
          }
        }
      } catch (e) {
        console.warn("useAutoCreateUser error:", e);
      }
    };

    createUserRow();
  }, [userId]);
}
